import type { AdminUser } from '@/api/types';
import type { FilterOption } from '@/components/ui';

// 사용자 관리 목록 필터 — 전체 목록(300명 안팎)을 받아 클라이언트에서 거른다.
// 모든 값은 URL 쿼리에 남겨 필터 걸린 화면을 링크로 공유할 수 있게 한다.

export const ALL = 'all';
// 그룹/팀 필터에서 'Peoply 소속 없음' 을 고르는 값.
export const NO_ORG = '__none__';

export type UserStatusFilter = typeof ALL | 'leader' | 'unregistered' | 'never_login';

export const STATUS_OPTIONS: FilterOption[] = [
  { value: ALL, label: '전체' },
  { value: 'leader', label: '리더만' },
  { value: 'unregistered', label: 'Peoply 미등록' },
  { value: 'never_login', label: '로그인 이력 없음' },
];

export interface UserFilterState {
  q: string;
  role: string; // 'all' | 'none'(guest) | 역할 키
  group: string;
  team: string;
  status: UserStatusFilter;
}

export const URL_KEYS = ['q', 'role', 'group', 'team', 'status'] as const;

export function readFilterState(params: URLSearchParams): UserFilterState {
  const status = params.get('status');
  return {
    q: params.get('q') ?? '',
    role: params.get('role') || ALL,
    group: params.get('group') || ALL,
    team: params.get('team') || ALL,
    status: STATUS_OPTIONS.some((o) => o.value === status) ? (status as UserStatusFilter) : ALL,
  };
}

// 검색 대상 — 이메일·이름·닉네임·Peoply 소속(그룹/팀)·수동 부서·직무.
function searchText(u: AdminUser): string {
  return [
    u.email,
    u.peoply_name,
    u.peoply_nickname,
    u.peoply_department,
    u.peoply_group,
    u.department,
    u.peoply_job,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

function matchesRole(u: AdminUser, role: string): boolean {
  if (role === ALL) return true;
  if (role === 'none') return u.roles.length === 0;
  return u.roles.includes(role);
}

function matchesOrg(value: string | null, filter: string): boolean {
  if (filter === ALL) return true;
  if (filter === NO_ORG) return !value;
  return value === filter;
}

function matchesStatus(u: AdminUser, status: UserStatusFilter): boolean {
  switch (status) {
    case 'leader':
      return u.peoply_is_leader;
    case 'unregistered':
      return !u.peoply_department;
    case 'never_login':
      return !u.last_login_at;
    default:
      return true;
  }
}

// 역할을 뺀 나머지 조건 — 역할별 인원 카드는 이 결과 기준으로 센다(현재 그룹/검색 안에서 몇 명인지).
export function filterExceptRole(users: AdminUser[], state: UserFilterState): AdminUser[] {
  const terms = state.q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return users.filter((u) => {
    if (!matchesOrg(u.peoply_group, state.group)) return false;
    if (!matchesOrg(u.peoply_team, state.team)) return false;
    if (!matchesStatus(u, state.status)) return false;
    if (terms.length === 0) return true;
    const text = searchText(u);
    // 여러 단어는 모두 포함(AND) — 예: "growth 리" 처럼 좁혀 가며 찾기.
    return terms.every((t) => text.includes(t));
  });
}

export function filterByRole(users: AdminUser[], role: string): AdminUser[] {
  return users.filter((u) => matchesRole(u, role));
}

function countOptions(values: (string | null)[], allLabel: string): FilterOption[] {
  const counts = new Map<string, number>();
  let none = 0;
  for (const v of values) {
    if (v) counts.set(v, (counts.get(v) ?? 0) + 1);
    else none += 1;
  }
  const sorted = [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  return [
    { value: ALL, label: allLabel },
    ...sorted.map(([v, n]) => ({ value: v, label: `${v} (${n})` })),
    ...(none > 0 ? [{ value: NO_ORG, label: `소속 없음 (${none})` }] : []),
  ];
}

export function groupOptions(users: AdminUser[]): FilterOption[] {
  return countOptions(users.map((u) => u.peoply_group), '전체 그룹');
}

// 팀 목록은 선택한 그룹 안에서만 — 그룹 직속(팀 없음) 인원은 '소속 없음' 대신 '그룹 직속' 으로 보인다.
export function teamOptions(users: AdminUser[], group: string): FilterOption[] {
  const inGroup = group === ALL ? users : users.filter((u) => matchesOrg(u.peoply_group, group));
  const options = countOptions(inGroup.map((u) => u.peoply_team), '전체 팀');
  if (group === ALL || group === NO_ORG) return options;
  return options.map((o) => (o.value === NO_ORG ? { ...o, label: o.label.replace('소속 없음', '그룹 직속') } : o));
}

// 화면 표시 이름 — 닉네임을 앞에 크게, 한글 이름은 옆에 작게(secondary).
// 닉네임이 없으면 한글 이름, 둘 다 없으면 이메일 앞부분을 쓴다.
export function displayName(u: AdminUser, fallback: string): { primary: string; secondary: string | null } {
  const primary = u.peoply_nickname || u.peoply_name || fallback;
  const secondary = u.peoply_name && u.peoply_name !== primary ? u.peoply_name : null;
  return { primary, secondary };
}
